import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, Save, ImageIcon, Send, CheckCircle2, AlertTriangle } from "lucide-react";

export default function BrandingPage() {
  const [branding, setBranding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifyState, setVerifyState] = useState(null); // {ok, message, from, when}
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    api.get("/branding").then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  const upd = (patch) => setBranding({ ...branding, ...patch });

  const save = async () => {
    setBusy(true);
    try {
      const { _id, key, updated_at, created_at, ...payload } = branding || {};
      await api.put("/branding", payload);
      toast.success("Branding saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const saveAndVerify = async () => {
    setVerifying(true);
    setVerifyState(null);
    const tid = toast.loading("Saving then sending a test email…");
    try {
      // 1) Save whatever the admin just typed so the /email-test uses the new value
      const { _id, key, updated_at, created_at, ...payload } = branding || {};
      await api.put("/branding", payload);
      // 2) Fire the diagnostic
      const r = await api.post("/invitations/email-test");
      const message = `Sent as "${r.data.sent_from}" to ${r.data.sent_to}. If it arrives, learner course links will be delivered.`;
      setVerifyState({ ok: true, message, from: r.data.sent_from, when: new Date().toISOString() });
      toast.success("Diagnostic sent — check your inbox", { id: tid, duration: 8000 });
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail, "Test email could not be sent");
      setVerifyState({ ok: false, message: detail, when: new Date().toISOString() });
      toast.error(detail, { id: tid, duration: 12000 });
    } finally {
      setVerifying(false);
    }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    upd({ logo_path: r.data.path });
    toast.success("Logo uploaded");
  };

  if (!branding) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 md:p-12 max-w-6xl">
      <header className="mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-3">/ Branding</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Identity</h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Set your company name, logo and brand colours. These appear on every certificate.
          </p>
        </div>
        <Button onClick={save} disabled={busy} data-testid="save-branding-button" className="rounded-sm h-11 bg-foreground text-background hover:bg-foreground/90">
          <Save size={16} strokeWidth={1.5} className="mr-2" /> {busy ? "Saving..." : "Save branding"}
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div>
            <Label className="text-xs font-bold tracking-[0.2em] uppercase">Company name</Label>
            <Input data-testid="branding-company-name" value={branding.company_name || ""} onChange={(e) => upd({ company_name: e.target.value })} className="mt-2 rounded-sm h-11" />
          </div>

          <div>
            <Label className="text-xs font-bold tracking-[0.2em] uppercase">Website URL (footer of slides)</Label>
            <Input data-testid="branding-website-url" value={branding.website_url || ""} onChange={(e) => upd({ website_url: e.target.value })} placeholder="www.trainingearlyyears.co.uk" className="mt-2 rounded-sm h-11 font-mono" />
          </div>

          <div>
            <Label className="text-xs font-bold tracking-[0.2em] uppercase">Logo</Label>
            <div className="mt-2 border border-border p-6 flex items-center gap-6">
              <div className="w-32 h-32 bg-secondary border border-border flex items-center justify-center overflow-hidden">
                {branding.logo_path ? (
                  <img src={fileUrl(branding.logo_path)} alt="" className="max-w-full max-h-full object-contain" />
                ) : (
                  <ImageIcon size={28} strokeWidth={1.2} className="text-muted-foreground" />
                )}
              </div>
              <label className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm cursor-pointer hover:bg-secondary transition-colors">
                <Upload size={14} strokeWidth={1.5} /> Upload logo
                <input
                  type="file"
                  accept="image/*"
                  data-testid="branding-logo-upload"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (f) await uploadLogo(f);
                  }}
                />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold tracking-[0.2em] uppercase">Primary colour</Label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="color"
                  data-testid="branding-primary-color-picker"
                  value={branding.primary_color || "#002e7a"}
                  onChange={(e) => upd({ primary_color: e.target.value })}
                  className="h-11 w-14 border border-border rounded-sm cursor-pointer"
                />
                <Input data-testid="branding-primary-color-input" value={branding.primary_color || ""} onChange={(e) => upd({ primary_color: e.target.value })} className="flex-1 rounded-sm h-11 font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-[0.2em] uppercase">Accent colour</Label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="color"
                  data-testid="branding-accent-color-picker"
                  value={branding.accent_color || "#002e7a"}
                  onChange={(e) => upd({ accent_color: e.target.value })}
                  className="h-11 w-14 border border-border rounded-sm cursor-pointer"
                />
                <Input data-testid="branding-accent-color-input" value={branding.accent_color || ""} onChange={(e) => upd({ accent_color: e.target.value })} className="flex-1 rounded-sm h-11 font-mono" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold tracking-[0.2em] uppercase">Certificate body text</Label>
            <Textarea data-testid="branding-cert-text" rows={2} value={branding.certificate_text || ""} onChange={(e) => upd({ certificate_text: e.target.value })} className="mt-2 rounded-sm" placeholder="e.g. has successfully completed the CPD course" />
          </div>

          <div className="pt-6 border-t border-border">
            <div className="eyebrow mb-3">/ Certificate template (optional)</div>
            <p className="text-xs text-muted-foreground mb-3">
              Upload a fully designed background (PNG or JPG, A4 landscape, 3508×2480 recommended).
              The candidate name, course title, date, CPD hours and score will be overlaid centred.
              Leave empty to use the built-in design.
            </p>
            <div className="border border-border p-4 flex items-center gap-6">
              <div className="w-40 aspect-[1.414/1] bg-secondary border border-border flex items-center justify-center overflow-hidden">
                {branding.certificate_template_path ? (
                  <img src={fileUrl(branding.certificate_template_path)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon size={24} strokeWidth={1.2} className="text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm cursor-pointer hover:bg-secondary transition-colors">
                  <Upload size={14} strokeWidth={1.5} /> Upload template
                  <input
                    type="file"
                    accept="image/*"
                    data-testid="branding-cert-template-upload"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try {
                        const fd = new FormData();
                        fd.append("file", f);
                        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        upd({ certificate_template_path: r.data.path });
                        toast.success("Template uploaded");
                      } catch {
                        toast.error("Upload failed");
                      }
                    }}
                  />
                </label>
                {branding.certificate_template_path && (
                  <button
                    type="button"
                    data-testid="branding-cert-template-clear"
                    onClick={() => upd({ certificate_template_path: null })}
                    className="text-xs font-mono text-destructive hover:underline text-left"
                  >
                    Remove template
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-border">
            <div className="eyebrow mb-3">/ Voice-over</div>
            <p className="text-xs text-muted-foreground mb-3">
              Candidates can listen to text &amp; PDF modules read aloud. By default this uses their device&apos;s
              free built-in voices. For broadcast-quality narration, generate professional audio per module
              from the course editor &mdash; uses OpenAI TTS via your Emergent Universal Key.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">Professional voice</Label>
                <select
                  data-testid="branding-tts-voice"
                  value={branding.tts_voice || "nova"}
                  onChange={(e) => upd({ tts_voice: e.target.value })}
                  className="mt-2 w-full border border-border rounded-sm h-11 px-2 text-sm bg-background"
                >
                  <option value="alloy">Alloy — neutral, balanced</option>
                  <option value="ash">Ash — clear, articulate</option>
                  <option value="coral">Coral — warm, friendly</option>
                  <option value="echo">Echo — smooth, calm</option>
                  <option value="fable">Fable — expressive, storytelling</option>
                  <option value="nova">Nova — energetic, upbeat</option>
                  <option value="onyx">Onyx — deep, authoritative</option>
                  <option value="sage">Sage — wise, measured</option>
                  <option value="shimmer">Shimmer — bright, cheerful</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">Quality</Label>
                <select
                  data-testid="branding-tts-model"
                  value={branding.tts_model || "tts-1"}
                  onChange={(e) => upd({ tts_model: e.target.value })}
                  className="mt-2 w-full border border-border rounded-sm h-11 px-2 text-sm bg-background"
                >
                  <option value="tts-1">Standard (faster, cheaper)</option>
                  <option value="tts-1-hd">HD (higher quality)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">Device-voice default speed</Label>
                <div className="mt-2 flex items-center gap-3">
                  <input
                    type="range"
                    min="0.5"
                    max="1.75"
                    step="0.05"
                    value={branding.voice_rate ?? 1}
                    onChange={(e) => upd({ voice_rate: parseFloat(e.target.value) })}
                    data-testid="branding-voice-rate"
                    className="flex-1"
                  />
                  <span className="font-mono text-xs w-12 text-right">{(branding.voice_rate ?? 1).toFixed(2)}x</span>
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">Default pitch</Label>
                <div className="mt-2 flex items-center gap-3">
                  <input
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.05"
                    value={branding.voice_pitch ?? 1}
                    onChange={(e) => upd({ voice_pitch: parseFloat(e.target.value) })}
                    data-testid="branding-voice-pitch"
                    className="flex-1"
                  />
                  <span className="font-mono text-xs w-12 text-right">{(branding.voice_pitch ?? 1).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-border">
            <div className="eyebrow mb-3">/ Email delivery</div>
            <p className="text-xs text-muted-foreground mb-4">
              Learner course links are sent by Emergent's managed email under your company name.
              Learner replies go to the reply-to address below, and the test email is sent there too.
            </p>

            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">Reply-to email</Label>
                <div className="mt-2 flex items-stretch gap-2 flex-wrap">
                  <Input
                    data-testid="branding-from-email"
                    type="email"
                    placeholder="training@earlyyearsacademy.co.uk"
                    value={branding.from_email || ""}
                    onChange={(e) => upd({ from_email: e.target.value })}
                    className="flex-1 min-w-[220px] rounded-sm h-11"
                  />
                  <Button
                    onClick={saveAndVerify}
                    disabled={verifying}
                    data-testid="branding-verify-sender"
                    variant="outline"
                    className="rounded-sm h-11 whitespace-nowrap"
                    title="Save this address and immediately send a diagnostic email to your own admin address so you can confirm Resend accepts it."
                  >
                    <Send size={14} strokeWidth={1.5} className="mr-2" />
                    {verifying ? "Testing…" : "Save & send test"}
                  </Button>
                </div>
                <div className="mt-2 flex items-center gap-3 flex-wrap font-mono text-[10px] text-muted-foreground">
                  <span data-testid="effective-sender">
                    Replies go to: <span className="text-foreground">{branding.from_email || "training@earlyyearsacademy.co.uk"}</span>
                  </span>
                </div>

                {verifyState && (
                  <div
                    data-testid={verifyState.ok ? "verify-success" : "verify-error"}
                    className={`mt-3 p-3 border rounded-sm text-xs ${
                      verifyState.ok
                        ? "border-foreground bg-secondary text-foreground"
                        : "border-destructive/40 bg-destructive/5 text-destructive"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {verifyState.ok
                        ? <CheckCircle2 size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                        : <AlertTriangle size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />}
                      <div className="flex-1">
                        <div className="font-semibold">
                          {verifyState.ok ? "Test email sent" : "Test email failed"}
                        </div>
                        <div className="mt-1 font-mono leading-snug">{verifyState.message}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">Email subject</Label>
                <Input
                  data-testid="branding-email-subject"
                  value={branding.email_subject || ""}
                  onChange={(e) => upd({ email_subject: e.target.value })}
                  className="mt-2 rounded-sm h-11"
                  placeholder="Your CPD course is ready"
                />
              </div>

              <div className="flex items-start justify-between gap-4 border border-border rounded-sm p-4">
                <div>
                  <Label className="text-xs font-bold tracking-[0.2em] uppercase">Course reminders</Label>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Learners who haven't finished get one friendly reminder 3 days after their link was emailed.
                    You can also switch reminders off for a single candidate on the Candidates page.
                  </p>
                </div>
                <Switch
                  checked={branding.reminders_enabled !== false}
                  onCheckedChange={(v) => upd({ reminders_enabled: v })}
                  data-testid="branding-reminders-toggle"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Certificate preview */}
        <div className="border border-border bg-secondary p-6 lg:sticky lg:top-6 lg:self-start">
          <div className="eyebrow mb-3">/ Live preview</div>
          {branding.certificate_template_path ? (
            <div className="bg-background aspect-[1.414/1] relative overflow-hidden">
              <img src={fileUrl(branding.certificate_template_path)} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="relative h-full w-full flex flex-col items-center justify-center text-center">
                <div className="text-xs">This is to certify that</div>
                <div style={{ color: branding.accent_color }} className="mt-1 text-2xl font-black tracking-tighter">Jane Candidate</div>
                <div className="mt-2 text-sm font-semibold" style={{ color: branding.primary_color }}>
                  &ldquo;Example Course Title&rdquo;
                </div>
                <div className="absolute bottom-4 inset-x-0 text-[10px] font-mono text-muted-foreground">
                  DATE: 2026-02-15 · CPD HOURS: 2.0 · SCORE: 90%
                </div>
              </div>
            </div>
          ) : (
            <div
              className="bg-background p-8 aspect-[1.414/1] flex flex-col items-center justify-center text-center"
              style={{ border: `2px solid ${branding.primary_color || "#002e7a"}` }}
            >
              <div
                className="border w-full h-full p-6 flex flex-col items-center justify-center"
                style={{ borderColor: branding.accent_color || "#002e7a" }}
              >
                {branding.logo_path && (
                  <img src={fileUrl(branding.logo_path)} alt="" className="max-h-14 mb-4 object-contain" />
                )}
                <div style={{ color: branding.primary_color }} className="text-xs uppercase tracking-[0.3em] font-bold">
                  Certificate of Completion
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">{branding.company_name || "Your company"}</div>
                <div className="mt-4 text-xs">This is to certify that</div>
                <div style={{ color: branding.accent_color }} className="mt-1 text-xl font-black tracking-tighter">Jane Candidate</div>
                <div className="mt-2 text-[11px] text-muted-foreground">{branding.certificate_text || "has successfully completed the CPD course"}</div>
                <div className="mt-2 text-sm font-semibold">&ldquo;Example Course Title&rdquo;</div>
                <div className="mt-4 text-[10px] font-mono text-muted-foreground">CPD HOURS: 2.0 · DATE: 2026-02-15</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
